import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Skeleton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ExternalIcon from '@mui/icons-material/OpenInNew';
import complianceReportService, {
  type AdminLicenseComplianceDto,
  type TenantComplianceReportDto,
  type UserComplianceSummaryDto,
} from '../../services/complianceReportService';
import type { MultiLicenseFulfillmentDto } from '../../services/licenseComplianceService';
import { getApiErrorInfo } from '../../services/api';
import ProvenanceBadge from './ProvenanceBadge';
import PrimaryLicenseChip from './PrimaryLicenseChip';
import LicenseFulfillmentPanel from './LicenseFulfillmentPanel';
import { isLicenseSetUp } from './licenseStatus';
import { formatUtcDate } from '../../utils/dateTime';

interface MemberComplianceDrawerProps {
  open: boolean;
  /** Tenant whose member is being inspected; fulfillment fetch is skipped when null. */
  tenantId: string | null;
  user: UserComplianceSummaryDto | null;
  report: TenantComplianceReportDto | null;
  onClose: () => void;
}

/** Compact row for licenses without a compliance requirement set up — the
 *  fulfillment endpoint only covers set-up licenses, so these render from the
 *  roster report data instead. */
function NotSetUpLicenseRow({ license }: { license: AdminLicenseComplianceDto }) {
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px',
        p: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap',
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '6px',
          bgcolor: 'brand.heroSurface',
          color: 'primary.contrastText',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.75rem',
          fontWeight: 700,
          letterSpacing: '0.02em',
          flexShrink: 0,
        }}
      >
        {license.stateCode}
      </Box>
      {license.isPrimary && <PrimaryLicenseChip memberView />}
      <ProvenanceBadge status={license.status} stateCode={license.stateCode} />
    </Box>
  );
}

/**
 * Right-side drill-down for a team member on the admin Team Reporting page:
 * per-license compliance cards, hours by category, credit detail, PDF download.
 */
export default function MemberComplianceDrawer({
  open,
  tenantId,
  user,
  report,
  onClose,
}: MemberComplianceDrawerProps) {
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const [fulfillment, setFulfillment] = useState<MultiLicenseFulfillmentDto | null>(null);
  const [fulfillmentLoading, setFulfillmentLoading] = useState(false);
  const [fulfillmentError, setFulfillmentError] = useState<string | null>(null);

  const userId = user?.userId ?? null;

  // Monotonic sequence so a late-resolving fetch for a previously selected
  // member can never overwrite the currently selected member's data.
  const requestSeqRef = useRef(0);

  const loadFulfillment = useCallback(async () => {
    if (!tenantId || !userId) return;
    const seq = ++requestSeqRef.current;
    setFulfillmentLoading(true);
    setFulfillmentError(null);
    try {
      const data = await complianceReportService.getUserFulfillment(tenantId, userId);
      if (seq !== requestSeqRef.current) return;
      setFulfillment(data);
    } catch (error) {
      if (seq !== requestSeqRef.current) return;
      setFulfillmentError(getApiErrorInfo(error).message);
    } finally {
      if (seq === requestSeqRef.current) setFulfillmentLoading(false);
    }
  }, [tenantId, userId]);

  useEffect(() => {
    // Reset stale data whenever the target member changes, then fetch on open.
    // Bumping the sequence also invalidates any in-flight request (e.g. when
    // the drawer closes before a fetch resolves).
    requestSeqRef.current++;
    setFulfillment(null);
    setFulfillmentError(null);
    setFulfillmentLoading(false);
    if (open && userId) {
      void loadFulfillment();
    }
  }, [open, userId, loadFulfillment]);

  const handleDownloadPdf = async () => {
    if (!user || !report) return;
    setDownloading(true);
    try {
      await complianceReportService.downloadUserPdf(user, report);
    } catch (error) {
      setDownloadError(`Failed to download report: ${getApiErrorInfo(error).message}`);
    } finally {
      setDownloading(false);
    }
  };

  const categoryEntries = user
    ? Object.entries(user.creditsByCategory).sort((a, b) => b[1] - a[1])
    : [];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 640 } } }}
    >
      {user && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2.5, pb: 2 }}>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                {user.fullName}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
                {user.email}
              </Typography>
            </Box>
            <IconButton onClick={onClose} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider />

          {/* Scrollable content */}
          <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2.5, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Per-license compliance — full fulfillment parity with the member's own view */}
            <Box>
              <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', mb: 1, lineHeight: 1.5 }}>
                License Compliance
              </Typography>
              {(user.licenses ?? []).length === 0 ? (
                <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                  No licenses on file for this member.
                </Typography>
              ) : fulfillmentLoading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Skeleton variant="rounded" height={180} />
                  {user.licenses.filter(isLicenseSetUp).length > 1 && (
                    <Skeleton variant="rounded" height={180} />
                  )}
                </Box>
              ) : fulfillmentError ? (
                <Alert
                  severity="error"
                  action={
                    <Button color="inherit" size="small" onClick={() => void loadFulfillment()}>
                      Retry
                    </Button>
                  }
                >
                  Failed to load license compliance: {fulfillmentError}
                </Alert>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {(fulfillment?.licenses ?? []).map((license) => {
                    // Resolve the primary license a derived (reciprocity) entry
                    // mirrors so the panel can name it in the summary line.
                    const source = license.reciprocitySourceLicenseId
                      ? fulfillment?.licenses.find(
                          (l) => l.licenseId === license.reciprocitySourceLicenseId
                        )
                      : undefined;
                    return (
                      <LicenseFulfillmentPanel
                        key={license.licenseId}
                        license={license}
                        reciprocitySourceName={source ? source.stateName || source.stateCode : null}
                      />
                    );
                  })}
                  {user.licenses
                    .filter((license) => !isLicenseSetUp(license))
                    .map((license) => (
                      <NotSetUpLicenseRow key={license.licenseId} license={license} />
                    ))}
                </Box>
              )}
            </Box>

            {/* Hours by category */}
            {categoryEntries.length > 0 && (
              <Box>
                <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', mb: 1, lineHeight: 1.5 }}>
                  Hours by Field of Study
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {/* Plain fill bars: these encode each category's share of the
                      member's hours, not progress toward a target, so no
                      progress-bar affordance (empty track implying a goal). */}
                  {categoryEntries.map(([cat, hrs]) => {
                    const maxHours = categoryEntries[0]?.[1] ?? 0;
                    return (
                      <Box key={cat} role="img" aria-label={`${cat}: ${hrs.toFixed(1)} hours`}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                          <Typography sx={{ fontSize: '13px' }}>{cat}</Typography>
                          <Typography sx={{ fontSize: '13px', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                            {hrs.toFixed(1)} hrs
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            height: 6,
                            borderRadius: '999px',
                            width: `${maxHours > 0 ? (hrs / maxHours) * 100 : 0}%`,
                            minWidth: 4,
                            bgcolor: 'primary.main',
                          }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* Credit detail */}
            <Box>
              <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', mb: 1, lineHeight: 1.5 }}>
                Credit Detail
              </Typography>
              {user.credits.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                  No credits recorded for this member.
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ pl: 0 }}>Course / Activity</TableCell>
                        <TableCell align="center">Hours</TableCell>
                        <TableCell align="right" sx={{ pr: 0 }}>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {user.credits.map((credit, i) => (
                        <TableRow key={i} sx={{ '& td': { fontSize: '13px' } }}>
                          <TableCell sx={{ pl: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {credit.source !== 'Platform' && (
                                <Tooltip title={`${credit.source} credit`}>
                                  <ExternalIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                </Tooltip>
                              )}
                              <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
                                {credit.courseTitle}
                              </Typography>
                            </Box>
                            {credit.creditType && (
                              <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>
                                {credit.creditType}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {credit.creditHours.toFixed(1)}
                          </TableCell>
                          <TableCell align="right" sx={{ pr: 0, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                            {formatUtcDate(credit.completedAt)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {user.credits.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    Total: {user.totalCreditHours.toFixed(1)} hrs
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Footer */}
          <Divider />
          <Box sx={{ p: 2.5 }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
              onClick={handleDownloadPdf}
              disabled={downloading}
            >
              {downloading ? 'Generating…' : 'Download report'}
            </Button>
          </Box>
        </Box>
      )}
      <Snackbar
        open={!!downloadError}
        autoHideDuration={6000}
        onClose={() => setDownloadError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setDownloadError(null)} severity="error" sx={{ width: '100%' }}>
          {downloadError}
        </Alert>
      </Snackbar>
    </Drawer>
  );
}
